// ParametersModal — панель пользовательских параметров (уравнения), как в Fusion.
//
// Таблица: Имя | Выражение | Значение | Ед. | Комментарий | ✕.
// Значение вычисляется парсером из parameters.js (ссылки на другие
// параметры, sin/cos/sqrt, арифметика). Ошибки показываются красным.

import React, { useMemo, useState } from 'react';
import useCadStore from '../store/useCadStore';
import { computeParameters } from '../parameters';
import { rebuildAllBodies } from '../rebuildPipeline';

let _seq = 0;
const newParamId = () => `param_${Date.now().toString(36)}_${_seq++}`;

const UNITS = ['мм', 'см', 'м', '°', ''];

export default function ParametersModal({ onClose }) {
    const parameters = useCadStore((s) => s.doc.parameters || []);
    const addParameter = useCadStore((s) => s.addParameter);
    const updateParameter = useCadStore((s) => s.updateParameter);
    const removeParameter = useCadStore((s) => s.removeParameter);

    // Черновик новой строки.
    const [draft, setDraft] = useState({ name: '', expression: '', unit: 'мм', comment: '' });
    const [applying, setApplying] = useState(false);

    // Пересобрать модель с новыми значениями параметров.
    const onApply = async () => {
        setApplying(true);
        try { await rebuildAllBodies(); }
        catch (e) { console.error('[Parameters] rebuild failed:', e); }
        finally { setApplying(false); }
    };

    const { values, errors } = useMemo(
        () => computeParameters(parameters),
        [parameters]
    );

    const nameValid = (name, selfId) => {
        if (!/^[a-zA-Zа-яА-Я_][a-zA-Zа-яА-Я0-9_]*$/.test(name)) return false;
        // Уникальность имени.
        return !parameters.some((p) => p.name === name && p.id !== selfId);
    };

    const onAdd = () => {
        const name = draft.name.trim();
        if (!nameValid(name, null)) return;
        addParameter({
            id: newParamId(),
            name,
            expression: draft.expression.trim() || '0',
            unit: draft.unit,
            comment: draft.comment.trim(),
        });
        setDraft({ name: '', expression: '', unit: draft.unit, comment: '' });
    };

    const cell = { padding: '4px 6px', borderBottom: '1px solid #33383f' };
    const inp = {
        width: '100%', background: '#20242b', border: '1px solid #363b44',
        color: '#e8ecf1', borderRadius: 4, padding: '3px 6px', font: '13px system-ui',
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(0,0,0,0.45)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div style={{
                width: 720, maxWidth: '92vw', maxHeight: '80vh', overflow: 'auto',
                background: '#1a1d23', border: '1px solid #363b44', borderRadius: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)', color: '#e8ecf1',
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', borderBottom: '1px solid #33383f',
                }}>
                    <b style={{ font: '15px system-ui' }}>Параметры</b>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', color: '#9aa4b2',
                        cursor: 'pointer', fontSize: 18,
                    }}>✕</button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', font: '13px system-ui' }}>
                    <thead>
                        <tr style={{ color: '#9aa4b2', textAlign: 'left' }}>
                            <th style={cell}>Имя</th>
                            <th style={cell}>Выражение</th>
                            <th style={cell}>Значение</th>
                            <th style={cell}>Ед.</th>
                            <th style={cell}>Комментарий</th>
                            <th style={cell}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {parameters.map((p) => {
                            const err = errors.get(p.id);
                            const val = values.get(p.name);
                            const badName = !nameValid(p.name, p.id);
                            return (
                                <tr key={p.id}>
                                    <td style={cell}>
                                        <input style={{ ...inp, borderColor: badName ? '#cc3344' : inp.border }}
                                            value={p.name}
                                            onChange={(e) => updateParameter(p.id, { name: e.target.value })}/>
                                    </td>
                                    <td style={cell}>
                                        <input style={{ ...inp, borderColor: err ? '#cc3344' : '#363b44' }}
                                            value={p.expression}
                                            title={err || ''}
                                            onChange={(e) => updateParameter(p.id, { expression: e.target.value })}/>
                                    </td>
                                    <td style={{ ...cell, color: err ? '#cc3344' : '#8fd88f', whiteSpace: 'nowrap' }}>
                                        {err ? 'ошибка' : (typeof val === 'number' ? +val.toFixed(4) : '—')}
                                    </td>
                                    <td style={cell}>
                                        <select style={inp} value={p.unit || ''}
                                            onChange={(e) => updateParameter(p.id, { unit: e.target.value })}>
                                            {UNITS.map((u) => <option key={u} value={u}>{u || '—'}</option>)}
                                        </select>
                                    </td>
                                    <td style={cell}>
                                        <input style={inp} value={p.comment || ''}
                                            onChange={(e) => updateParameter(p.id, { comment: e.target.value })}/>
                                    </td>
                                    <td style={cell}>
                                        <button onClick={() => removeParameter(p.id)} style={{
                                            background: 'none', border: 'none', color: '#cc6666',
                                            cursor: 'pointer',
                                        }} title="Удалить параметр">✕</button>
                                    </td>
                                </tr>
                            );
                        })}
                        {/* Строка добавления */}
                        <tr style={{ background: '#20242b' }}>
                            <td style={cell}>
                                <input style={inp} placeholder="Width" value={draft.name}
                                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && onAdd()}/>
                            </td>
                            <td style={cell}>
                                <input style={inp} placeholder="40 или Width*2" value={draft.expression}
                                    onChange={(e) => setDraft({ ...draft, expression: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && onAdd()}/>
                            </td>
                            <td style={cell}/>
                            <td style={cell}>
                                <select style={inp} value={draft.unit}
                                    onChange={(e) => setDraft({ ...draft, unit: e.target.value })}>
                                    {UNITS.map((u) => <option key={u} value={u}>{u || '—'}</option>)}
                                </select>
                            </td>
                            <td style={cell}>
                                <input style={inp} placeholder="комментарий" value={draft.comment}
                                    onChange={(e) => setDraft({ ...draft, comment: e.target.value })}/>
                            </td>
                            <td style={cell}>
                                <button onClick={onAdd}
                                    disabled={!nameValid(draft.name.trim(), null)}
                                    style={{
                                        background: '#2f7fd0', border: 'none', color: '#fff',
                                        borderRadius: 4, padding: '3px 10px', cursor: 'pointer',
                                        opacity: nameValid(draft.name.trim(), null) ? 1 : 0.4,
                                    }}>+</button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 16px', gap: 12,
                }}>
                    <span style={{ color: '#9aa4b2', font: '12px system-ui' }}>
                        Выражения: <code>+ - * / ^</code>, <code>sin cos sqrt min max round</code> (углы в °),
                        ссылки на параметры по имени. Пример: <code>Height = Width * 1.5</code>.
                    </span>
                    <button onClick={onApply} disabled={applying} style={{
                        background: '#2aa84a', border: 'none', color: '#fff',
                        borderRadius: 4, padding: '6px 14px', cursor: 'pointer',
                        whiteSpace: 'nowrap', opacity: applying ? 0.6 : 1,
                    }}>{applying ? 'Пересчёт…' : 'Применить и перестроить'}</button>
                </div>
            </div>
        </div>
    );
}
